import { useSignal, type Signal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { useContext, useEffect, useRef } from 'preact/hooks';

import { interpolate } from '@amumin/i18n';
import {
  Button,
  InfoButton,
  Notice,
  PageBar,
  PageHeader as UiPageHeader,
  SectionHeader,
  showToast,
} from '@amumin/ui';
import { cx, Icon, type IconId } from '@amumin/ui-core';

import { errorMessage, isApiError } from '../api.js';
import { HubTabs } from './hub.js';
import { activePersonal, HUBS, hubTabs, navLabel, type HubId } from './navigation.js';
import { HubContext, SheetPageContext, topBack, type SheetFrame } from './pageContext.js';
import {
  appText,
  bumpData,
  dataVersion,
  handleAuthLoss,
  messages,
  refreshApprovals,
} from '../state/session.js';
import { createLatestGate } from './latest.js';
import { StepUpCancelled } from './stepUp.js';

// Kleine gedeelde bouwstenen voor de schermen: laden met fout/opnieuw, mutaties met toast en
// "Ongedaan maken" (DESIGN.md §2.6 vergevingsgezind), en een paginakop met één primaire actie.

export interface Loaded<T> {
  data: Signal<T | null>;
  error: Signal<string | undefined>;
  loading: Signal<boolean>;
  reload: () => Promise<void>;
}

/**
 * Laadt data bij het openen en na elke mutatie (dataVersion). Fouten worden één regel tekst;
 * 401 stuurt naar /login. `deps` herladen ook (bv. een andere week). Alleen het antwoord van het
 * laatste verzoek telt: snel wisselen (Dag → Maand, weekpijlen) toont nooit een oud bereik.
 */
export function useLoad<T>(fn: () => Promise<T>, deps: ReadonlyArray<unknown> = []): Loaded<T> {
  const data = useSignal<T | null>(null);
  const error = useSignal<string | undefined>(undefined);
  const loading = useSignal(true);
  const gate = useRef(createLatestGate());
  const reload = async () => {
    loading.value = true;
    const latest = await gate.current.run(
      fn,
      (value) => {
        data.value = value;
        error.value = undefined;
      },
      (e) => {
        if (!handleAuthLoss(e)) error.value = errorMessage(e, messages.value);
      },
    );
    if (latest) loading.value = false;
  };
  useEffect(() => {
    void reload();
    // Bewust: alleen deps + dataVersion (fn is elke render nieuw).
  }, [...deps, dataVersion.value]);
  return { data, error, loading, reload };
}

/** Breed scherm? (media query, volgt het venster live). */
export function useWide(minWidth = 720): boolean {
  const query = `(min-width: ${minWidth}px)`;
  const wide = useSignal(typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => (wide.value = mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return wide.value;
}

/** Leest/schrijft een voorkeur in localStorage; privémodus of blokkade → stil de standaard. */
export function readPref<T extends string>(key: string, allowed: ReadonlyArray<T>, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v && (allowed as ReadonlyArray<string>).includes(v) ? (v as T) : fallback;
  } catch {
    return fallback;
  }
}

export function writePref(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // geen opslag: dan onthouden we het niet
  }
}

/** Toont een fout als toast (geen rode lap in de pagina); 401 → login, step-up geannuleerd → stil. */
export function toastError(e: unknown): void {
  if (e instanceof StepUpCancelled) return;
  if (handleAuthLoss(e)) return;
  showToast({ kind: 'error', message: errorMessage(e, messages.value) });
}

export interface MutateOptions {
  /** Toasttekst bij succes. */
  success?: string;
  /** "Ongedaan maken" in de toast (8 s). */
  undo?: () => Promise<unknown>;
  /** Tekst na een geslaagde undo. */
  undone?: string;
  /** Krijgt de sluitfunctie van de succestoast (bv. om een oudere melding weg te halen). */
  onToast?: (dismiss: () => void) => void;
}

/**
 * Fout bij een mutatie: toast, en bij een versieconflict (409) de schermen echt opnieuw laden,
 * zoals de melding belooft ("De lijst is opnieuw geladen"). Zonder dat bleef bv. het afspraakblad
 * de oude rijder tonen terwijl de ander de rit al had overgenomen (review c-fs r2).
 */
export function mutationFailed(e: unknown): void {
  toastError(e);
  if (isApiError(e, 409, 'version_conflict')) bumpData();
}

/** Voert een mutatie uit, ververst de schermen en toont een toast (met Ongedaan maken). */
export async function mutate<T>(
  run: () => Promise<T>,
  options: MutateOptions = {},
): Promise<T | null> {
  try {
    const result = await run();
    bumpData();
    void refreshApprovals();
    if (options.success) {
      const undo = options.undo;
      const dismiss = showToast({
        kind: 'success',
        message: options.success,
        ...(undo
          ? {
              actionLabel: appText.value.common.undo,
              onAction: () => {
                undo()
                  .then(() => {
                    bumpData();
                    void refreshApprovals();
                    if (options.undone) showToast({ message: options.undone });
                  })
                  .catch(mutationFailed);
              },
            }
          : {}),
      });
      options.onToast?.(dismiss);
    }
    return result;
  } catch (e) {
    mutationFailed(e);
    return null;
  }
}

/** Terug naar de ouderpagina: een link, of (zoals in een gesprek op de telefoon) een knop. */
export interface PageBack {
  href?: string | undefined;
  onPress?: () => void | undefined;
  label: string;
  /** data-name van de pijl (standaard page-back). */
  name?: string | undefined;
}

export interface PageHeaderOptions {
  title: string;
  subtitle?: ComponentChildren | undefined;
  actions?: ComponentChildren | undefined;
  tools?: ComponentChildren | undefined;
  /** Oud: wordt nu `meta` (onder de titel). */
  eyebrow?: ComponentChildren | undefined;
  meta?: ComponentChildren | undefined;
  back?: PageBack | undefined;
  /** Uitleg achter de (i)-knop. Niet bij een toestemming (geloof, gezondheid, bank). */
  info?: ComponentChildren | undefined;
  infoTitle?: string | undefined;
  name?: string | undefined;
}

/**
 * Paginakop van de app (WP1/WP2, docs/ux/ontwerpsysteem.md): de PageHeader van @amumin/ui met de
 * teksten van de app. Eén rij van 56 px: titel, (i)-knop, rechts `tools` (hooguit twee
 * pictogramknoppen) en `actions` (de ene primaire knop). Er staat nooit iets boven de titel:
 * `eyebrow` (oud) en `meta` komen als één regel eronder; lange uitleg hoort in `info`.
 *
 * WP2: de titel staat op elke pagina op exact dezelfde plek, ook met een terugpijl. Die pijl staat
 * op de telefoon in de bovenbalk en op de computer in de marge vóór de titel (shell.css). In een
 * samengevoegde pagina (hub) wordt de kop die van de hub, met de tabbladen eronder; als sectie in
 * een hub een sectiekop; in een blad (routeSheet) gaat de titel naar de kop van het blad. In de
 * eigen omgeving (Mijn omgeving) staat onder de titel een label "Privé", geen banner erboven.
 */
export function PageHeader(raw: PageHeaderOptions) {
  const hub = useContext(HubContext);
  const sheet = useContext(SheetPageContext);
  const props = shortSubtitle(raw);
  if (sheet) return <SheetPageHead {...props} sheet={sheet} />;
  if (hub?.mode === 'section') return <SectionPageHead {...props} />;
  return <MainPageHead {...props} hub={hub?.id} />;
}

/** Langer dan dit is geen ondertitel meer maar uitleg (één regel klein, ontwerpsysteem.md). */
const SUBTITLE_MAX = 64;

/**
 * Afronding "één geheel": een lange ondertitel (een zin uitleg) gaat achter de (i), zodat elke kop
 * hooguit één regel onder de titel heeft. Heeft de pagina al uitleg, dan blijft de ondertitel staan.
 */
function shortSubtitle(props: PageHeaderOptions): PageHeaderOptions {
  const sub = props.subtitle;
  if (typeof sub !== 'string' || sub.length <= SUBTITLE_MAX || props.info) return props;
  return { ...props, subtitle: undefined, info: <p>{sub}</p> };
}

function infoProps(info: ComponentChildren, title: string) {
  const m = appText.value;
  return { label: m.common.info, title, closeLabel: m.common.close, children: info };
}

/** De terugpijl (link of knop), zonder de titel te verschuiven. */
function BackArrow({ back, cls, name }: { back: PageBack; cls: string; name: string }) {
  const icon = <Icon id="arrow-left" size={22} />;
  return back.href ? (
    <a class={cls} href={back.href} aria-label={back.label} title={back.label} data-name={name}>
      {icon}
    </a>
  ) : (
    <button
      type="button"
      class={cls}
      aria-label={back.label}
      title={back.label}
      data-name={name}
      onClick={() => back.onPress?.()}
    >
      {icon}
    </button>
  );
}

function MainPageHead({
  title,
  subtitle,
  actions,
  tools,
  eyebrow,
  meta,
  back,
  info,
  infoTitle,
  name,
  hub,
}: PageHeaderOptions & { hub?: HubId | undefined }) {
  // Terug op de telefoon: de bovenbalk (Shell) toont de pijl zolang deze kop er staat. `latest`
  // houdt de nieuwste klik-functie bij zonder het signaal bij elke weergave te vervangen.
  const owner = useRef({});
  const latest = useRef(back);
  latest.current = back;
  useEffect(() => {
    if (!back) return undefined;
    topBack.value = {
      href: back.href,
      onPress: back.onPress ? () => latest.current?.onPress?.() : undefined,
      label: back.label,
      owner: owner.current,
    };
    return () => {
      if (topBack.value?.owner === owner.current) topBack.value = null;
    };
  }, [back?.href, back?.label, !!back?.onPress]);

  const def = hub ? HUBS[hub] : undefined;
  const shownTitle = def ? navLabel(def.title) : title;
  const tabs = hub ? hubTabs(hub) : [];
  const metaText = meta ?? eyebrow;
  const personal = activePersonal();
  // Het label alleen als de pagina zelf geen regel onder de titel heeft: Geldzaken en Kompas (WP5)
  // zetten hun eigen privé-regel daar; de paarse rand van de bovenbalk of zijbalk staat er altijd.
  const metaLine =
    personal && !metaText ? (
      <span class="space-label" data-name="space-label-personal">
        {appText.value.omgeving.spaces.privateHint}
      </span>
    ) : (
      metaText
    );
  return (
    <>
      <div class="page-head-wrap" data-space={personal ? 'personal' : undefined}>
        {back ? (
          <BackArrow back={back} cls="page-back-hang" name={back.name ?? 'page-back'} />
        ) : null}
        <UiPageHeader
          title={shownTitle}
          info={info ? infoProps(info, infoTitle ?? shownTitle) : undefined}
          tools={tools}
          primary={actions}
          subtitle={subtitle}
          meta={metaLine}
          name={name}
        />
      </div>
      {hub && tabs.length > 1 ? (
        <PageBar name="hub-bar">
          <HubTabs id={hub} />
        </PageBar>
      ) : null}
    </>
  );
}

/** Binnen een hub als tweede scherm (Sterren): een sectiekop (h2) met dezelfde knoppen. */
function SectionPageHead({
  title,
  subtitle,
  actions,
  tools,
  eyebrow,
  meta,
  info,
  infoTitle,
  name,
}: PageHeaderOptions) {
  const right =
    tools || actions ? (
      <div class="page-actions">
        {tools}
        {actions}
      </div>
    ) : undefined;
  return (
    <div class="hub-section-head" data-name={name}>
      <SectionHeader
        title={
          <>
            {title}
            {info ? <InfoButton {...infoProps(info, infoTitle ?? title)} name="page-info" /> : null}
          </>
        }
        action={right}
      />
      {(meta ?? eyebrow) ? <p class="page-meta">{meta ?? eyebrow}</p> : null}
      {subtitle ? <p class="page-subtitle">{subtitle}</p> : null}
    </div>
  );
}

/** In een blad: de titel staat in de kop van het blad; hier alleen de rest van de kop. */
function SheetPageHead({
  title,
  subtitle,
  actions,
  tools,
  eyebrow,
  meta,
  info,
  infoTitle,
  name,
  sheet,
}: PageHeaderOptions & { sheet: SheetFrame }) {
  useEffect(() => {
    sheet.setTitle(title);
  }, [title]);
  const line = meta ?? eyebrow;
  if (!subtitle && !line && !info && !tools && !actions) return null;
  return (
    <div class="sheet-page-head" data-name={name}>
      {line || subtitle || info ? (
        <div class="sheet-page-text">
          {line ? <p class="page-meta">{line}</p> : null}
          {subtitle ? <p class="page-subtitle">{subtitle}</p> : null}
          {info ? <InfoButton {...infoProps(info, infoTitle ?? title)} name="page-info" /> : null}
        </div>
      ) : null}
      {tools || actions ? (
        <div class="page-actions">
          {tools}
          {actions}
        </div>
      ) : null}
    </div>
  );
}

/** "+n meer" onder een kaart (Card `more`), in de taal van de app. */
export function moreLabel(n: number): string {
  return interpolate(appText.value.common.moreCount, { n });
}

/** Link die eruitziet als een knop (navigatie is een <a>, geen <button> in een <a>). */
export function LinkButton({
  href,
  children,
  variant = 'default',
  icon,
  size = 'normal',
  name,
}: {
  href: string;
  children: ComponentChildren;
  variant?: 'default' | 'primary';
  icon?: IconId;
  size?: 'small' | 'normal';
  name?: string;
}) {
  return (
    <a
      href={href}
      class={cx(
        'link-button',
        variant === 'primary' && 'link-button-primary',
        size === 'small' && 'link-button-small',
      )}
      data-name={name}
      data-btn={size}
    >
      {icon ? <Icon id={icon} size={size === 'small' ? 16 : 20} /> : null}
      <span>{children}</span>
    </a>
  );
}

/** Laadfout met één knop "Opnieuw proberen" (DESIGN.md §8.X). */
export function LoadError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Notice kind="error" name="load-error">
      <span>{message} </span>
      <Button name="retry" variant="link" onPress={onRetry}>
        {appText.value.common.retry}
      </Button>
    </Notice>
  );
}

/** Rustige laadplaceholder: grijze blokken i.p.v. een spinner (geen flikkering). */
export function Skeleton({ rows = 3 }: { rows?: number }) {
  const list = [];
  for (let i = 0; i < rows; i++) list.push(<span key={i} class="skeleton-row" />);
  return (
    <div class="skeleton" aria-busy="true" aria-label={appText.value.common.loading}>
      {list}
    </div>
  );
}

export { EmptyState } from './EmptyState.js';
